/**
 * One instanced, camera-facing cloud of soft round particles — the drawable
 * half of `flight/particles.ts`, and ENHANCED EFFECTS' whole GPU footprint.
 *
 * **One material, one mesh, one draw call per cloud, no texture at all.** A
 * particle is a unit quad carrying three instanced attributes: a colour, a
 * screen-space-independent world radius (folded into the instance matrix) and
 * an opacity. The round soft edge is computed in the fragment shader from the
 * quad's own `uv` — `1 - 2·|uv - ½|` — so there is no sprite sheet to load, no
 * second decode, and no `TextureLoader` in the hot path. The fire cloud and the
 * smoke cloud are two instances of this class and that is the entire +2 draw
 * calls the effect costs, however many aeroplanes are alight.
 *
 * **Opaque, `alphaTest`, ordered dither — no exceptions.** This renderer's
 * reversed-Z depth buffer has now eaten two separate effects that reached for
 * `transparent: true` / `depthWrite: false` (`docs/engineering/lessons.md`,
 * 2026-08-22, twice), and `three/screen-sprites.ts`'s header states the rule
 * that came out of it: a fade is re-expressed as a stipple, the material stays
 * depth-writing and order-independent. This file obeys it rather than
 * re-litigating it — the soft radial falloff and the per-particle opacity are
 * multiplied into one coverage value and cut against the shared 4×4 Bayer
 * ladder (`ditherThreshold`, imported so the two systems dissolve on the same
 * threshold and cannot moiré against each other).
 *
 * That also happens to be the more period-honest reading. A 1990 blitter
 * cannot blend, and what it *can* do — and what Dynamix's own art does all over
 * this game — is dither one colour against another. A stippled, palette-
 * quantised particle is a modern effect drawn the way the machine of the day
 * would have drawn it.
 *
 * **Nothing here allocates per frame.** `begin`/`push`/`end` write straight
 * into the instanced buffers through module-level scratch, the way
 * `screen-sprites.ts` and `flight/combat-visuals.ts` do.
 */

import * as THREE from "three/webgpu";
import { ditherThreshold } from "./ordered-dither.ts";

/**
 * The coverage cut. Binary either side of the dither `step`, so any value in
 * the open interval works; a half cut is the same number every other material
 * in this renderer uses.
 */
const PARTICLE_ALPHA_TEST = 0.5;

const scratchMatrix = new THREE.Matrix4();
const scratchPosition = new THREE.Vector3();
const scratchScale = new THREE.Vector3();
const cameraPosition = new THREE.Vector3();
const cameraQuaternion = new THREE.Quaternion();
const cameraScale = new THREE.Vector3();
// Shared by every cloud, for the reason `screen-sprites.ts` records for its own
// camera basis: a frame places one camera and begins every pool against it, so
// the last `begin` and every `push` after it agree.

/**
 * Distance from the quad's centre, 0 at the middle and 1 at the inscribed
 * circle. `uv` is `[0,1]²`, so `2·|uv − ½|` is the unit square's own
 * coordinates and its length is the radius.
 */
function radialDistance(): ReturnType<typeof THREE.TSL.dot> {
  const centred = THREE.TSL.uv().sub(THREE.TSL.vec2(0.5, 0.5)).mul(2);
  return THREE.TSL.length(centred);
}

/**
 * The material every cloud shares the shape of.
 *
 * Exported so `apps/web/test/particle-material.test.ts` can pin the flags that
 * two separate lanes have now had to rediscover the hard way, without needing a
 * DOM or a GPU — the same arrangement `sheetMaterial` and `bulletTracerMaterial`
 * have with their own guard tests.
 */
export function particleMaterial(): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide });
  material.colorNode = THREE.TSL.attribute<"vec3">("instanceTint", "vec3");
  // `smoothstep(1, 0.35, d)` is 1 across the core and falls to 0 at the rim, so
  // a particle reads as a soft ball rather than a square with a hole cut in it.
  const softness = THREE.TSL.smoothstep(1, 0.35, radialDistance());
  const coverage = softness.mul(THREE.TSL.attribute<"float">("instanceOpacity", "float"));
  material.opacityNode = THREE.TSL.step(ditherThreshold(), coverage);
  material.alphaTest = PARTICLE_ALPHA_TEST;
  // Fire is a light and smoke is a few metres of soot at gun range; neither is
  // a lit surface, and this port has no `scene.fog` to tint them with anyway
  // (`flight/sky-dressing.ts` records why). Same call `bulletTracerMaterial`
  // makes for the tracers.
  material.fog = false;
  return material;
}

function instancedAttribute(capacity: number, components: number): THREE.InstancedBufferAttribute {
  const attribute = new THREE.InstancedBufferAttribute(new Float32Array(capacity * components), components);
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}

/**
 * A fixed-capacity cloud of world-sized soft particles.
 *
 * A class rather than a factory of closures for the reason `ScreenSpritePool`
 * is one: it holds mutable buffers and four methods, which is past the size the
 * 2026-08-19 MULTIPLAYER lesson puts on the closure-factory pattern.
 */
export class ParticleCloud {
  readonly mesh: THREE.InstancedMesh;
  private readonly tints: THREE.InstancedBufferAttribute;
  private readonly opacities: THREE.InstancedBufferAttribute;
  private written = 0;

  constructor(capacity: number) {
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.tints = instancedAttribute(capacity, 3);
    this.opacities = instancedAttribute(capacity, 1);
    geometry.setAttribute("instanceTint", this.tints);
    geometry.setAttribute("instanceOpacity", this.opacities);
    this.mesh = new THREE.InstancedMesh(geometry, particleMaterial(), capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // A cloud spans wherever the fight is, and its instances move every frame;
    // culling the whole mesh against a bounding sphere computed once at
    // construction would drop it the moment the fight left the origin.
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    this.mesh.visible = false;
  }

  /**
   * Reads the camera's orientation for this frame. It must already be placed —
   * the 2026-08-19 gunfire-geometry lesson's rule 4, the same one
   * `ScreenSpritePool.begin` states.
   */
  begin(camera: THREE.Camera): void {
    camera.updateWorldMatrix(true, false);
    camera.matrixWorld.decompose(cameraPosition, cameraQuaternion, cameraScale);
    this.written = 0;
  }

  /**
   * Places one particle. `radiusM` is a world half-extent, not a screen size:
   * unlike the sprite pools — whose fixed pixel sizes are the original blitter's
   * own behaviour — a particle is a physical volume of flame or soot and has to
   * shrink with distance, which is what makes a burning aeroplane read as a
   * burning aeroplane at 500 m instead of a lit match.
   *
   * Returns `false` when the cloud is full or the particle is invisible, which
   * a caller may treat the same way (draw nothing).
   */
  push(x: number, y: number, z: number, radiusM: number, r: number, g: number, b: number, opacity: number): boolean {
    if (this.written >= this.mesh.instanceMatrix.count) return false;
    if (opacity <= 0 || radiusM <= 0) return false;
    scratchPosition.set(x, y, z);
    scratchScale.set(radiusM * 2, radiusM * 2, 1);
    scratchMatrix.compose(scratchPosition, cameraQuaternion, scratchScale);
    this.mesh.setMatrixAt(this.written, scratchMatrix);
    this.tints.setXYZ(this.written, r, g, b);
    this.opacities.setX(this.written, opacity);
    this.written += 1;
    return true;
  }

  /** Publishes this frame's writes. An empty cloud is hidden outright, so it costs no draw call at all. */
  end(): void {
    this.mesh.count = this.written;
    this.mesh.visible = this.written > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.tints.needsUpdate = true;
    this.opacities.needsUpdate = true;
  }

  /** How many particles the last `end()` published — what a draw-call count and the tests read. */
  get liveCount(): number {
    return this.written;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    // Freeing the material is what releases this render object's bindings under
    // the WebGPU backend (2026-08-19 world-stability lesson), not bookkeeping.
    (this.mesh.material as THREE.Material).dispose();
  }
}
